import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { MapPin, ShieldCheck, Flag, ChevronLeft, CalendarDays, User2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { api, CATEGORIES, waLink, formatPrice, formatDate, formatApiError, imgSrc } from "@/lib/api";
import { PageLoading } from "@/App";
import ListingCard from "@/components/ListingCard";
import { toast } from "sonner";
import Reveal from "@/components/Reveal";

export default function ListingDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [listing, setListing] = useState(null);
  const [state, setState] = useState("loading");
  const [reason, setReason] = useState("");
  const [reporting, setReporting] = useState(false);

  useEffect(() => {
    let alive = true;
    setState("loading");
    api
      .get(`/listings/${id}`)
      .then((r) => {
        if (!alive) return;
        setListing(r.data);
        setState("ok");
      })
      .catch(() => alive && setState("notfound"));
    return () => {
      alive = false;
    };
  }, [id]);

  const report = async () => {
    setReporting(true);
    try {
      await api.post(`/listings/${id}/report`, { reason });
      toast.success("Bildiriminiz moderasyon ekibine iletildi. Teşekkürler.");
      setReason("");
      document.querySelector("[data-report-close]")?.click();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setReporting(false);
    }
  };

  if (state === "loading") return <PageLoading />;
  if (state === "notfound" || !listing) {
    return (
      <div className="mx-auto max-w-xl px-4 py-32 text-center">
        <h1 className="font-display text-3xl font-black">İlan bulunamadı</h1>
        <p className="mt-3 text-slate-500">Bu ilan kaldırılmış veya onay beklemiyor olabilir.</p>
        <button onClick={() => navigate("/")} data-testid="back-home-button" className="mt-8 rounded-full bg-coral px-7 py-3 text-sm font-bold text-white hover:bg-coraldark transition">
          Ana Sayfaya Dön
        </button>
      </div>
    );
  }

  const catName = CATEGORIES.find((c) => c.id === listing.category)?.name || listing.category;
  const notApproved = listing.status !== "approved";

  return (
    <div className="mx-auto max-w-7xl px-4 sm:px-6 pt-24 pb-16">
      <button data-testid="detail-back-button" onClick={() => navigate(-1)} className="inline-flex items-center gap-1.5 text-sm font-semibold text-slate-500 hover:text-coral transition mb-6">
        <ChevronLeft size={17} /> Geri
      </button>

      {notApproved && (
        <div className="mb-6 rounded-2xl border border-amber2/50 bg-amber2/10 px-5 py-4 text-sm font-semibold text-amber-800">
          Bu ilan yayınlanmadı — şu durumda: <strong>{listing.status === "pending" ? "Onay Bekliyor" : listing.status === "revision" ? "Revizyon İstenen" : "Reddedildi"}</strong>. Bu sayfayı yalnızca siz ve moderasyon ekibi görebilir.
        </div>
      )}

      <div className="grid gap-10 lg:grid-cols-[1.15fr_0.85fr]">
        <Reveal>
          <div className="clip-frame overflow-hidden rounded-3xl bg-slate-100">
            <img data-testid="detail-main-image" src={imgSrc(listing.images?.[0], 1200)} alt={listing.title} decoding="async" className="aspect-[4/3] w-full object-cover" />
          </div>
          {listing.images && listing.images.length > 1 && (
            <div className="mt-3 grid grid-cols-4 gap-3">
              {listing.images.slice(0, 4).map((img, i) => (
                <img key={i} src={imgSrc(img, 300)} alt={`${listing.title} ${i + 1}`} loading="lazy" className="aspect-square w-full rounded-xl border border-black/10 object-cover" />
              ))}
            </div>
          )}
          <div className="mt-6 rounded-3xl border border-black/5 bg-card p-6" data-testid="detail-description">
            <h2 className="font-display text-lg font-bold mb-3">İlan Açıklaması</h2>
            <p className="whitespace-pre-wrap text-[15px] leading-relaxed text-slate-700">{listing.description}</p>
          </div>
        </Reveal>

        <div className="space-y-5">
          <Reveal delay={0.05}>
            <div className="rounded-3xl border border-black/5 bg-card p-6 shadow-sm">
              <div className="flex flex-wrap items-center gap-2">
                <span className="rounded-full bg-emerald2 px-3 py-1 text-[11px] font-bold text-bone">{listing.district}, Zonguldak</span>
                <span className="rounded-full border border-coal/15 px-3 py-1 text-[11px] font-bold text-slate-600">{catName}</span>
                <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 border border-emerald-200 px-3 py-1 text-[11px] font-bold text-emerald-700">
                  <ShieldCheck size={12} /> Onaydan Geçmiş
                </span>
              </div>
              <h1 data-testid="detail-title" className="mt-4 font-display text-2xl sm:text-3xl font-black tracking-tight leading-tight">{listing.title}</h1>
              <p data-testid="detail-price" className="mt-3 font-mono text-3xl sm:text-4xl font-bold text-emerald-600">{formatPrice(listing.price)}</p>
              <p className="mt-2 inline-flex items-center gap-1.5 text-xs text-slate-400">
                <CalendarDays size={13} /> {formatDate(listing.created_at)} tarihinde eklendi
              </p>

              <a
                data-testid="whatsapp-contact-button"
                href={waLink(listing.phone, listing.title)}
                target="_blank" rel="noopener noreferrer"
                className="mt-6 flex w-full items-center justify-center gap-2.5 rounded-full bg-wa px-6 py-4 text-base font-bold text-white shadow-lg shadow-wa/30 hover:brightness-110 hover:-translate-y-0.5 transition-all"
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
                WhatsApp ile İletişime Geç
              </a>
              <p className="mt-3 text-center text-[11px] leading-relaxed text-slate-400">
                Görüşmeniz doğrudan WhatsApp üzerinden kurulur. Ödeme göndermeden önce ürünü yerinde görmeyi unutmayın.
              </p>
            </div>
          </Reveal>

          <Reveal delay={0.1}>
            <div className="rounded-3xl border border-black/5 bg-card p-6" data-testid="detail-seller-card">
              <div className="flex items-center gap-3.5">
                <span className="grid h-12 w-12 place-items-center rounded-2xl bg-emerald2 text-bone font-display font-black text-lg">
                  {listing.seller?.name?.[0]?.toUpperCase() || "A"}
                </span>
                <div>
                  <p className="font-display font-bold flex items-center gap-1.5">
                    {listing.seller?.name || "Alsat67 Kullanıcısı"} <ShieldCheck size={15} className="text-wa" />
                  </p>
                  <p className="text-xs text-slate-500 inline-flex items-center gap-1">
                    <User2 size={12} /> Üyelik: {formatDate(listing.seller?.member_since)} • <MapPin size={12} className="text-coral" /> {listing.district}
                  </p>
                </div>
              </div>
            </div>
          </Reveal>

          <Reveal delay={0.15}>
            <div className="rounded-3xl border border-black/5 bg-slate-50 p-6">
              <p className="text-xs leading-relaxed text-slate-500">
                <strong className="text-slate-700">Güvenlik notu:</strong> Alsat67 yalnızca ilan yayınlama aracıdır; alım-satım işlemlerinde aracı değildir.
                Kişisel verileriniz KVKK kapsamında korunur. Uygunsuz ilanları bize bildirin.
              </p>
              <Dialog>
                <DialogTrigger asChild>
                  <Button data-testid="report-listing-button" variant="ghost" className="mt-3 text-slate-500 hover:text-red-600 hover:bg-red-50 text-sm gap-1.5 px-3">
                    <Flag size={14} /> Bu ilanı bildir
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>İlanı Bildir</DialogTitle>
                  </DialogHeader>
                  <p className="text-sm text-slate-500">Bu ilanın neden uygunsuz olduğunu kısaca yazın. Moderasyon ekibimiz en kısa sürede inceler.</p>
                  <Textarea data-testid="report-reason-input" value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Örn: Fiyat gerçek dışı, ürün sahte görünüyor..." rows={4} />
                  <Button data-testid="report-submit-button" disabled={reporting || reason.trim().length < 5} onClick={report} className="bg-coral hover:bg-coraldark text-white">
                    {reporting ? "Gönderiliyor..." : "Bildirimi Gönder"}
                  </Button>
                </DialogContent>
              </Dialog>
            </div>
          </Reveal>
        </div>
      </div>
    </div>
  );
}
